import { defineComponent, ref } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { box, container, loader, loadingButton, pageTitle } from '@/ui/components';
import { useTask } from '@/ui/concerns';
import { create } from '@/services/api/resources/group';
import { filter as fetchAllUsers } from '@/services/api/resources/user';
import { filterListToOptions, toastAndRedirect } from '@/ui/helpers/uiHelper';
import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
import { SelectOptions } from '@/ui/types';
import { useGroupForm } from './useGroupForm';
import { useGroupRecord } from './useGroupRecord';
import { renderForbiddenPage } from '../error/ForbiddenPage';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';
import { useCreatableUserModal } from '@/ui/pages/useCreatableUserModal';
import { AdministrationTabNames, queryTabName } from '@/ui/components/navigation/constants';

export const GroupCreatePage = defineComponent({
  name: 'GroupCreatePage',
  setup() {
    if (!hasFeature('groups')) {
      return renderResourceTeaserPage(t('common.groups'));
    }

    if (!hasPermission(Permissions.MANAGE_USERS)) {
      return renderForbiddenPage;
    }

    const userOptions = ref<SelectOptions>([]);

    // create empty record
    const record = useGroupRecord();

    // get record
    const getRecord = useTask(
      () => fetchAllUsers(),
      users => {
        userOptions.value = filterListToOptions(users);
      },
    );

    // send record
    const sendRecord = useTask(
      () => create(record.value),
      () => {
        toastAndRedirect(t('group.created'), 'administration', { [queryTabName]: AdministrationTabNames.GROUP });
      },
    );

    // form
    const { formFields, handleSubmit } = useGroupForm(record, sendRecord);

    useCreatableUserModal(userOptions, id => {
      record.value.users.push(id);
    });

    return function render() {
      return container(
        [
          pageTitle(t('group.create')),
          box([
            loader('form', getRecord, [
              ...formFields(userOptions.value),
              loadingButton(
                t('group.create'),
                handleSubmit,
                sendRecord.state.value.pending,
              ),
            ]),
          ]),
        ],
        'narrow',
      );
    };
  },
});
